import { FC, useEffect, useState } from 'react';
import { Property, PortfolioSimulation, PortfolioSummary, SimulationParameters, UserSettings } from '@/types/index';
import { portfolioService } from '@services/portfolio.service';
import { authService } from '@services/auth-service';
import { Modal } from '@components/feedback/Modal';
import { SimulationForm } from './SimulationForm';

interface Props {
  property: Property;
}

const fmtChf = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `CHF ${new Intl.NumberFormat('fr-CH').format(Math.round(v))}`;
const fmtPct = (v: number | null | undefined) =>
  v == null || isNaN(v) ? '—' : `${v.toFixed(2)} %`;

const KpiChip: FC<{ label: string; value: string; positive?: boolean; negative?: boolean }> = (
  { label, value, positive, negative }
) => (
  <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 min-w-[100px]">
    <span className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 text-center whitespace-nowrap">{label}</span>
    <span className={`text-xs font-bold ${positive ? 'text-green-600 dark:text-green-400' : negative ? 'text-red-500 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
      {value}
    </span>
  </div>
);

const DeltaBadge: FC<{ label: string; before: string; after: string; positive: boolean }> = ({ label, before, after, positive }) => (
  <div className="flex flex-col gap-0.5 px-2 py-1 rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-[10px]">
    <span className="text-gray-400 dark:text-gray-500">{label}</span>
    <div className="flex items-center gap-1">
      <span className="text-gray-500 dark:text-gray-400">{before}</span>
      <span className="text-gray-400">→</span>
      <span className={`font-bold ${positive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
        {after}
      </span>
    </div>
  </div>
);

const SimulationCard: FC<{
  sim: PortfolioSimulation;
  portfolioSummary?: PortfolioSummary;
  onEdit: (sim: PortfolioSimulation) => void;
  onDelete: (simId: string) => void;
  isDeleting: boolean;
}> = ({ sim, portfolioSummary, onEdit, onDelete, isDeleting }) => {
  const r = sim.results;

  const cfBefore = portfolioSummary ? portfolioSummary.totalCashFlow / 12 : null;
  const cfAfter = portfolioSummary ? (portfolioSummary.totalCashFlow + r.cashFlow) / 12 : null;
  const ltvBefore = portfolioSummary?.globalLTV ?? null;
  const ltvAfter = portfolioSummary && portfolioSummary.totalMarketValue > 0
    ? (portfolioSummary.totalDebt + r.loanAmount) / (portfolioSummary.totalMarketValue + (sim.parameters.purchasePrice ?? 0))
    : null;
  const noiBefore = portfolioSummary ? portfolioSummary.totalNOI : null;
  const noiAfter = portfolioSummary ? portfolioSummary.totalNOI + r.NOI : null;

  return (
    <div className="flex flex-col gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{sim.name}</span>
          <span className="text-[10px] text-gray-400 shrink-0">
            {new Date(sim.createdAt).toLocaleDateString('fr-CH')}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onEdit(sim)}
            className="p-1 rounded-lg text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-colors"
            title="Modifier ce scénario"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(sim.id)}
            disabled={isDeleting}
            className="p-1 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 transition-colors disabled:opacity-40"
            title="Supprimer ce scénario"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <KpiChip label="Cash-flow / mois" value={fmtChf(r.cashFlowMonthly)}
          positive={r.cashFlowMonthly > 0} negative={r.cashFlowMonthly < 0} />
        <KpiChip label="LTV" value={`${(r.LTV * 100).toFixed(1)} %`}
          positive={r.LTV <= 0.65} negative={r.LTV > 0.8} />
        <KpiChip label="Rendement net" value={fmtPct(r.netYield)} />
        <KpiChip label="Cash-on-cash" value={fmtPct(r.cashOnCash)}
          positive={(r.cashOnCash ?? 0) > 0} negative={(r.cashOnCash ?? 0) < 0} />
        <KpiChip label="NOI / an" value={fmtChf(r.NOI)}
          positive={r.NOI > 0} negative={r.NOI <= 0} />
        <KpiChip label="Fonds propres" value={fmtChf(r.equityRequired)} />
        <KpiChip label="Montant prêt" value={fmtChf(r.loanAmount)} />
      </div>

      {portfolioSummary && cfBefore != null && cfAfter != null && (
        <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-700">
          <span className="text-[10px] text-gray-400 self-center mr-1">Impact portefeuille :</span>
          <DeltaBadge label="Cash-flow/mois total"
            before={fmtChf(cfBefore)} after={fmtChf(cfAfter)}
            positive={cfAfter >= cfBefore} />
          {ltvAfter != null && (
            <DeltaBadge label="LTV globale"
              before={ltvBefore != null ? `${(ltvBefore * 100).toFixed(1)} %` : '—'}
              after={`${(ltvAfter * 100).toFixed(1)} %`}
              positive={ltvAfter <= (ltvBefore ?? 0)} />
          )}
          {noiBefore != null && noiAfter != null && (
            <DeltaBadge label="NOI annuel total"
              before={fmtChf(noiBefore)} after={fmtChf(noiAfter)}
              positive={noiAfter >= noiBefore} />
          )}
        </div>
      )}
    </div>
  );
};

export const CatalogSimulationPanel: FC<Props> = ({ property }) => {
  const [simulations, setSimulations] = useState<PortfolioSimulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingSimulation, setEditingSimulation] = useState<PortfolioSimulation | null>(null);
  const [defaultAmortizationPct, setDefaultAmortizationPct] = useState(1.0);
  const [userSettings, setUserSettings] = useState<UserSettings | undefined>(undefined);
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | undefined>(undefined);

  useEffect(() => {
    Promise.all([
      authService.getSettings(),
      portfolioService.getSummary(),
    ]).then(([settings, summary]) => {
      setUserSettings(settings);
      setDefaultAmortizationPct(settings.amortizationRate);
      setPortfolioSummary(summary);
    }).catch(() => {});
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await portfolioService.getCatalogSimulations(property.id);
      setSimulations(data);
    } catch (err) {
      console.error('Erreur chargement scénarios:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [property.id]);

  const handleSave = async (name: string, parameters: SimulationParameters) => {
    setIsSaving(true);
    try {
      if (editingSimulation) {
        await portfolioService.updateCatalogSimulation(property.id, editingSimulation.id, { name, parameters });
      } else {
        await portfolioService.createCatalogSimulation(property.id, { name, parameters });
      }
      setIsFormOpen(false);
      setEditingSimulation(null);
      await load();
    } catch (err) {
      console.error('Erreur sauvegarde scénario:', err);
      alert('Erreur lors de la sauvegarde du scénario');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (sim: PortfolioSimulation) => {
    setEditingSimulation(sim);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingSimulation(null);
  };

  const handleDelete = async (simId: string) => {
    if (!confirm('Supprimer ce scénario ?')) return;
    setDeletingId(simId);
    try {
      await portfolioService.deleteCatalogSimulation(property.id, simId);
      setSimulations(prev => prev.filter(s => s.id !== simId));
    } catch (err) {
      console.error('Erreur suppression scénario:', err);
      alert('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Simulez différents scénarios d'acquisition pour ce bien et mesurez leur impact sur votre portefeuille.
        </p>
        <button
          onClick={() => { setEditingSimulation(null); setIsFormOpen(true); }}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouveau scénario
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-400">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Chargement des scénarios…
        </div>
      ) : simulations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
          <span className="text-3xl">🎯</span>
          <p className="text-sm font-medium">Aucun scénario enregistré</p>
          <p className="text-xs">Créez votre premier scénario pour simuler cette acquisition.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {simulations.map(sim => (
            <SimulationCard
              key={sim.id}
              sim={sim}
              portfolioSummary={portfolioSummary}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDeleting={deletingId === sim.id}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingSimulation
          ? `Modifier — ${editingSimulation.name}`
          : `Nouveau scénario — ${property.address ?? property.city}`}
        size="xl"
      >
        <SimulationForm
          catalogProperty={property}
          userSettings={userSettings}
          portfolioSummary={portfolioSummary}
          defaultAmortizationPct={defaultAmortizationPct}
          editingSimulation={editingSimulation ?? undefined}
          onSave={handleSave}
          onCancel={handleFormClose}
          isSaving={isSaving}
        />
      </Modal>
    </div>
  );
};
